const DEFAULT_TIMEOUT_MS = 1200;
const FALLBACK_PORTS = [5173, 3000, 3002];
const DEFAULT_ALLOWED_LOCAL_HOSTS = ['localhost', '127.0.0.1'];

function isLocalHost(value) {
  return DEFAULT_ALLOWED_LOCAL_HOSTS.includes((value || '').toLowerCase());
}

function normalizeUrl(input) {
  if (typeof input !== 'string') {
    return null;
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  const hasProtocol = /^https?:\/\//i.test(trimmed);

  let withProtocol = trimmed;
  if (!hasProtocol) {
    const hostOnly = trimmed.split('/')[0].split(':')[0].toLowerCase();
    withProtocol = `${isLocalHost(hostOnly) ? 'http' : 'https'}://${trimmed}`;
  }

  try {
    const parsed = new URL(withProtocol);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }

    return parsed.href.replace(/\/$/, '');
  } catch (error) {
    return null;
  }
}

function buildFallbackCandidates() {
  const hostname = window.location.hostname || 'localhost';

  return FALLBACK_PORTS
    .map((port) => normalizeUrl(`http://${hostname}:${port}`))
    .filter(Boolean);
}

function getAllowedHosts() {
  const configured = (process.env.REACT_APP_PORTFOLIO_ALLOWED_HOSTS || '')
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  const hostname = (window.location.hostname || '').toLowerCase();

  return Array.from(new Set([
    ...configured,
    ...DEFAULT_ALLOWED_LOCAL_HOSTS,
    ...(hostname ? [hostname] : []),
  ]));
}

function isAllowedHost(normalizedUrl) {
  if (!normalizedUrl) {
    return false;
  }

  try {
    const host = new URL(normalizedUrl).hostname.toLowerCase();
    return getAllowedHosts().includes(host);
  } catch (_) {
    return false;
  }
}

function toHostOrigin(url) {
  try {
    return new URL(url).origin;
  } catch (_) {
    return null;
  }
}

function getCandidateUrls() {
  const candidates = [];
  const seen = new Set();

  const addCandidate = (value) => {
    const normalized = normalizeUrl(value);
    if (!normalized || seen.has(normalized) || !isAllowedHost(normalized)) {
      return;
    }

    seen.add(normalized);
    candidates.push(normalized);
  };

  addCandidate(process.env.REACT_APP_PORTFOLIO_URL);
  buildFallbackCandidates().forEach(addCandidate);

  return candidates;
}

async function isUrlReachable(url, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const target = toHostOrigin(url) || url;

    await fetch(target, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });

    return true;
  } catch (error) {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function resolvePortfolioUrl(options = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const candidates = getCandidateUrls();

  for (const candidate of candidates) {
    const reachable = await isUrlReachable(candidate, timeoutMs);
    if (reachable) {
      return candidate;
    }
  }

  return null;
}

export { normalizeUrl, getCandidateUrls, isAllowedHost, getAllowedHosts };
